/**
 * Shared vocabulary for the rule editors. These used to be file-local to
 * `AutoTransactionForm`; they moved here when the library gained its own forms,
 * which edit the same `ConditionDto` / `EditActionDto[]` shapes and would
 * otherwise have had to fork a copy.
 */
import type {
  ConditionDto,
  ConditionalActionMatcherDto,
  EditActionDto,
  ExpenseTypeId,
  FieldName,
  GroupId,
  Operator,
  SourceId,
} from "../../api/types";
import { makeDateString } from "../../api/types";
import type { PickerKind } from "../EntityPickerField/config";
import { makeMoney } from "../../logic/money";

export const PROMPTABLE_FIELDS: ReadonlySet<FieldName> = new Set<FieldName>([
  "source",
  "merchant",
  "description",
  "group",
  "amount",
  "expenseType",
  "datePaid",
]);

// A prompt prefix seeds a free-text box with the shared lead-in of a value
// ("Parkering - ") so the user only types the part that varies. It has no
// meaning for a date, an amount or an entity picker, so it is only offered
// for the text-valued fields.
export const PREFIXABLE_FIELDS: ReadonlySet<FieldName> = new Set<FieldName>([
  "merchant",
  "description",
]);

// Empty-id sentinel. Once the user picks (or inline-creates) an entity, the
// real id replaces it; until then the picker shows no selection and the
// backend rejects the action with a validation error if submitted blank.
export const EMPTY_ID = "" as never;

export const ENTITY_ACTION_LABELS: Partial<
  Record<EditActionDto["field"], string>
> = {
  source: "Source",
  group: "Group",
  expenseType: "Expense Type",
  addCategory: "Add Category",
  removeCategory: "Remove Category",
  applyActionSet: "Shared actions",
};

// Every field a value can be read *from*. Entity fields read as their display
// name; the rest as the text already shown in the UI.
export const COPYABLE_FROM_FIELDS: readonly FieldName[] = [
  "source",
  "group",
  "expenseType",
  "merchant",
  "description",
  "amount",
  "datePaid",
  "kid",
  "payToAccount",
];

/**
 * The fields drawn from a registry, and which picker serves each. These are
 * the fields that match *an entry*, by id, rather than whatever text was
 * typed — so they get a dropdown and survive a rename. Everything else
 * (merchant, description, KID, pay-to) is free text with no list to pick from.
 */
export const ENTITY_FIELD_KINDS: Partial<Record<FieldName, PickerKind>> = {
  source: "source",
  group: "group",
  expenseType: "expense-type",
};

export const isEntityField = (field: FieldName): boolean =>
  field in ENTITY_FIELD_KINDS;

/**
 * Operators that test a field with something the author wrote, as opposed to
 * naming an entry outright. Only these can be a typo, so only these get a
 * running count of what they hit.
 */
export const isPatternOperator = (operator: Operator): boolean =>
  operator === "contains" || operator === "regex" || operator === "equal";

const OPERATOR_LABELS: Partial<Record<Operator, string>> = {
  is: "Is",
  isOneOf: "Is one of",
  contains: "Contains",
  regex: "Regex",
  equal: "Equals",
};

/**
 * On an entity field, `equal` compares against typed text where `is` names the
 * entry — so it is labelled for what it now is. On a free-text field it is
 * still plain equality and reads as such.
 */
export const operatorLabel = (field: FieldName, operator: Operator): string =>
  operator === "equal" && isEntityField(field)
    ? "Legacy equals (text)"
    : (OPERATOR_LABELS[operator] ?? operator);

const offeredStringOperators = (field: FieldName): Operator[] =>
  isEntityField(field)
    ? ["is", "isOneOf", "contains", "regex"]
    : ["equal", "contains", "regex"];

/**
 * The operators offered for a text-valued field.
 *
 * Entity fields lead with the picker-backed pair; `equal` is not among them,
 * because free-typing a fixed value there is exactly what `is` replaces. A
 * rule that already uses it keeps it listed — otherwise the select would show
 * a blank and silently rewrite the rule on the next save.
 */
export const stringOperators = (
  field: FieldName,
  current: Operator,
): Operator[] => {
  const offered = offeredStringOperators(field);
  return offered.includes(current) ? offered : [...offered, current];
};

const isPickerOperator = (operator: Operator): boolean =>
  operator === "is" || operator === "isOneOf";

/**
 * The value to carry over when the operator changes.
 *
 * An id is not a pattern and a pattern is not an id, so crossing between the
 * picker operators and the text ones starts the value fresh — otherwise
 * switching "is" to "contains" would leave a ULID sitting in a text box,
 * matching nothing. Narrowing a multi-selection to "is" keeps the first pick,
 * which is what someone changing their mind about "one of" meant.
 */
export const valueForOperatorChange = (
  from: Operator,
  to: Operator,
  value: string,
): string => {
  if (isPickerOperator(from) !== isPickerOperator(to)) return "";
  if (from === "isOneOf" && to === "is")
    return value.split(",").filter(Boolean)[0] ?? "";
  return value;
};

export const defaultOperatorForField = (field: FieldName): Operator => {
  if (field === "amount") return "equal";
  if (field === "datePaid") return "onDay";
  return isEntityField(field) ? "is" : "contains";
};

// The fields matched against typed text, all offered the same operators.
const FREE_TEXT_FIELDS: ReadonlySet<FieldName> = new Set<FieldName>([
  "merchant",
  "description",
  "kid",
  "payToAccount",
]);

/**
 * The operator and value to carry over when a condition's field changes.
 *
 * Moving between two free-text fields keeps what was typed — "Rema" under
 * Merchant is just as much a pattern under Description — and keeps the
 * operator when the new field offers it. One it doesn't falls back to the
 * field's default, carrying the value only as far as `valueForOperatorChange`
 * would. Anything to or from an entity, amount or date field starts fresh:
 * an id, a number and a day are not interchangeable with text or each other.
 */
export const conditionForFieldChange = (
  from: FieldName,
  to: FieldName,
  operator: Operator,
  value: string,
): { operator: Operator; value: string } => {
  const fallback = defaultOperatorForField(to);
  if (!FREE_TEXT_FIELDS.has(from) || !FREE_TEXT_FIELDS.has(to))
    return { operator: fallback, value: "" };
  if (offeredStringOperators(to).includes(operator)) return { operator, value };
  return {
    operator: fallback,
    value: valueForOperatorChange(operator, fallback, value),
  };
};

export const FIELD_LABELS: Record<FieldName, string> = {
  source: "Source",
  merchant: "Merchant",
  description: "Description",
  group: "Group",
  amount: "Amount",
  expenseType: "Expense Type",
  datePaid: "Date Paid",
  kid: "KID",
  payToAccount: "Pay To Account",
};

export const MATCHER_LABELS: Record<
  ConditionalActionMatcherDto["field"],
  string
> = {
  source: "Source",
  group: "Group",
  expenseType: "Expense Type",
  category: "Category",
  retired: "Retired field",
};

/**
 * Names the actions still missing a value. The backend rejects a blank entity
 * id as an unparseable body ("Invalid JSON request body"), which says nothing
 * about which picker to go back to — so catch it here and say it plainly.
 */
export const unsetActionLabels = (actions: EditActionDto[]): string[] =>
  actions.flatMap((action) => {
    if (action.field === "conditional") {
      const matcherLabel = MATCHER_LABELS[action.value.condition.field];
      return [
        // A list, so "unset" is an empty one — which is truthy, and would
        // sail past a bare check the way a blank id does not.
        ...(action.value.condition.value.length > 0
          ? []
          : [`the conditional action's "If ${matcherLabel}"`]),
        ...unsetActionLabels([action.value.action]),
      ];
    }
    // Every arm of a match names entities the same way a conditional does,
    // and an armless match is refused by the backend, so both are caught here
    // with a message that says which arm to go back to.
    if (action.field === "match") {
      const label =
        MATCHER_LABELS[action.value.arms[0]?.condition.field ?? "source"];
      if (action.value.arms.length === 0) return ["the match's arms"];
      return action.value.arms.flatMap((arm, index) => [
        ...(arm.condition.value.length > 0
          ? []
          : [`the match's "${label}" in arm ${index + 1}`]),
        ...unsetActionLabels([arm.action]),
      ]);
    }
    // A citation is unset in two ways: no set picked, or a set picked whose
    // inputs haven't been filled in. Both leave the rule dead on save, so name
    // whichever applies rather than letting the round-trip do it.
    if (action.field === "applyActionSet") {
      if (!action.value.set) return [ENTITY_ACTION_LABELS.applyActionSet!];
      return action.value.inputs
        .filter((input) => input.value.trim() === "")
        .map((input) => `the shared actions' "${input.name}"`);
    }
    const label = ENTITY_ACTION_LABELS[action.field];
    // The category actions carry a list, where "unset" is an empty array —
    // which is truthy, so it needs its own check rather than falling into the
    // blank-id one.
    const unset = Array.isArray(action.value)
      ? action.value.length === 0
      : !action.value;
    return label && unset ? [label] : [];
  });

/**
 * Names the unpicked "shared match" nodes in a condition tree. The backend
 * rejects a reference to a set that doesn't exist, but its message names an id
 * rather than a place in the form — so catch it here and say where to look.
 */
export const unsetConditionLabels = (
  condition: ConditionDto,
  position?: number,
): string[] => {
  if (condition.type === "ref") return condition.set ? [] : ["a shared match"];
  // Named by `retiredConditionLabels`: it has no value to pick.
  if (condition.type === "retired") return [];
  if (condition.type === "field") {
    // Every operator needs a value — an id to pick, a number or day to
    // parse, or a pattern to test — and the backend refuses each with a
    // message about operators or parsing; name the field instead. The text
    // operators are the ones that matter most: changing a condition's field
    // to or from a non-text one clears its value, and a `contains ""` left behind matches the whole
    // ledger (#462).
    if (condition.value.trim() !== "") return [];
    const where = position === undefined ? "" : ` in position ${position}`;
    return [`the ${FIELD_LABELS[condition.field]} condition${where}`];
  }
  // Positioned within the enclosing group, the way an arm is numbered.
  return condition.conditions.flatMap((child, index) =>
    unsetConditionLabels(child, index + 1),
  );
};

/**
 * Names the actions that still target a retired field. The backend loads
 * such an action as a no-op and reports the rule as dead, so it is refused
 * here, where the message can say which action to remove.
 */
export const retiredActionLabels = (actions: EditActionDto[]): string[] =>
  actions.flatMap((action) => {
    if (action.field === "retired")
      return [`the retired ${action.value} action`];
    if (action.field === "conditional") {
      const matcher = action.value.condition;
      return [
        ...(matcher.field === "retired"
          ? [`the conditional action's retired ${matcher.value} "If"`]
          : []),
        ...retiredActionLabels([action.value.action]),
      ];
    }
    if (action.field === "match") {
      return action.value.arms.flatMap((arm, index) => [
        ...(arm.condition.field === "retired"
          ? [`the match's retired ${arm.condition.value} in arm ${index + 1}`]
          : []),
        ...retiredActionLabels([arm.action]),
      ]);
    }
    return [];
  });

/**
 * Names the conditions on a retired field in a tree, positioned the way
 * `unsetConditionLabels` positions an empty one. A shared match set is
 * checked where it is edited, not in every rule citing it.
 */
export const retiredConditionLabels = (
  condition: ConditionDto,
  position?: number,
): string[] => {
  if (condition.type === "retired") {
    const where = position === undefined ? "" : ` in position ${position}`;
    return [`the retired ${condition.field} condition${where}`];
  }
  if (condition.type === "field" || condition.type === "ref") return [];
  return condition.conditions.flatMap((child, index) =>
    retiredConditionLabels(child, index + 1),
  );
};

/**
 * Joins the unset labels for the "Pick a value for …" message: "X",
 * "X and Y", "X, Y and Z". British English, as the American form would put a
 * comma before the "and".
 */
export const joinLabels = (labels: string[]): string =>
  new Intl.ListFormat("en-GB", { type: "conjunction" }).format(labels);

export const defaultActionForField = (fieldName: FieldName): EditActionDto => {
  switch (fieldName) {
    case "source":
      return { field: "source", value: EMPTY_ID as SourceId };
    case "merchant":
      return { field: "merchant", value: "" };
    case "description":
      return { field: "description", value: "" };
    case "group":
      return { field: "group", value: EMPTY_ID as GroupId };
    case "amount":
      return { field: "amount", value: makeMoney(0) };
    case "expenseType":
      return { field: "expenseType", value: EMPTY_ID as ExpenseTypeId };
    case "datePaid":
      return {
        field: "datePaid",
        value: makeDateString(new Date().toISOString().slice(0, 10)),
      };
    case "kid":
    case "payToAccount":
      // Import-sourced, read-only: matchable in conditions but never an action.
      throw new Error(`${fieldName} is read-only and cannot be a rule action`);
  }
};
